// BLOCCO DEL BROWSER CON DOPPIO PIN — negazione plausibile anche sul PC.
// Due PIN: quello reale apre Lattice, quello di copertura apre una sessione esca vuota.
// I PIN non si salvano: si salva solo la loro impronta (PBKDF2-SHA256, 200.000 giri, con
// sale casuale) in questo browser. Chi guarda il disco non può distinguere i due casi,
// e chi guarda lo schermo dopo il PIN di copertura vede una Lattice senza nulla dentro.
//
// AUDIT v3.1.0 (secondo giro) — tre cose non tornavano:
//  1. il FILE CHIAVE (la chiave privata ML-DSA-65) restava in chiaro in `sessionStorage`
//     anche a scheda bloccata: sotto il PIN di copertura si leggeva dalla console. Peggio
//     di un token: è l'identità, non una sessione.
//  2. per lo stesso motivo l'app, dopo il blocco, si RI-AUTENTICAVA da sola (il ciclo dei
//     pallini rigenerava un token di sessione): il blocco non tagliava nulla.
//  3. lo stato "sbloccato" viveva in `sessionStorage`, quindi un semplice F5 rientrava
//     senza PIN.
// CURA: lo stato di sblocco vive SOLO in RAM (un ricaricamento richiede il PIN); il file
// chiave e i token, al blocco, restano solo in una cassetta cifrata con una chiave
// derivata dal PIN REALE (AES-256-GCM). Il PIN di copertura non la apre: per lui quella
// cassetta è rumore, e non c'è niente altro da leggere.
const K = "lat.weblock.v1";
const S = "lat.weblock.session";
const BOX = "lat.weblock.box"; // cassetta cifrata: file chiave + token, per-scheda
const RT_PLAIN = "lattice_keyfile_rt"; // copia in chiaro del file chiave (vault.js)
// ── DOVE VIVONO I TOKEN DI SESSIONE ──────────────────────────────────────────
// Quando il doppio PIN è attivo, il token della sessione e l'identità NON devono
// sopravvivere alla chiusura del browser: altrimenti alla riapertura, sotto il PIN di
// copertura, sarebbero ancora leggibili dalla console e il token autorizzerebbe il
// server. Con il lucchetto attivo stanno solo in sessionStorage (per-scheda, volatile);
// senza lucchetto restano in localStorage come prima, per non togliere la comodità a chi
// non usa la negazione plausibile.
const TOK_KEYS = ["lattice_token", "lattice_sc_token", "lattice_user"];

// Stato della scheda: SOLO in RAM. Muore col ricaricamento della pagina, e quindi un F5
// non è più una via per rientrare senza PIN.
let sessState = null;
// Chiave che apre la cassetta, derivata dal PIN reale. Vive in RAM e muore col blocco.
let wrapKey = null;
// Ponte verso il custode del file chiave (`lib/vault.js`), per non creare un import
// circolare: è vault.js che si registra qui.
let vault = { get: () => null, set: () => {}, wipe: () => {} };
export function bindVault(v) { vault = { ...vault, ...v }; }

export function tokGet(k) {
  try {
    const sv = sessionStorage.getItem(k);
    if (sv != null) return sv;
  } catch { /* niente */ }
  try { return localStorage.getItem(k); } catch { return null; }
}
export function tokSet(k, v) {
  try {
    if (lockEnabled()) {
      sessionStorage.setItem(k, v);
      try { localStorage.removeItem(k); } catch { /* niente */ }
      return;
    }
  } catch { /* niente */ }
  try { localStorage.setItem(k, v); } catch { /* niente */ }
}
export function tokDel(k) {
  try { sessionStorage.removeItem(k); } catch { /* niente */ }
  try { localStorage.removeItem(k); } catch { /* niente */ }
}

// All'accensione del lucchetto: i token già presenti in localStorage vanno spostati in
// sessionStorage e cancellati dal disco. La scheda corrente continua a funzionare.
function tokensToSession() {
  for (const k of TOK_KEYS) {
    try {
      const v = localStorage.getItem(k);
      if (v != null) { sessionStorage.setItem(k, v); localStorage.removeItem(k); }
    } catch { /* niente */ }
  }
}
// Allo spegnimento: si riportano su disco, così il login torna persistente.
function tokensToLocal() {
  for (const k of TOK_KEYS) {
    try {
      const v = sessionStorage.getItem(k);
      if (v != null) localStorage.setItem(k, v);
    } catch { /* niente */ }
  }
}


const enc = new TextEncoder();
const hex = (b) => Array.from(new Uint8Array(b)).map((x) => x.toString(16).padStart(2, "0")).join("");
const unhex = (s) => new Uint8Array((String(s).match(/../g) || []).map((h) => parseInt(h, 16)));

// AUDIT v5.2 (A-04): 200.000 giri erano sotto la raccomandazione OWASP 2023 per
// PBKDF2-HMAC-SHA256 (600.000). I giri ora stanno nella configurazione: chi ha già un
// lucchetto continua a verificarlo con i suoi (cambiarli glielo renderebbe inapribile),
// i lucchetti nuovi nascono a 600.000.
const ITER = 600000;
const ITER_LEGACY = 200000;
const iterOf = (cfg) => (cfg && Number(cfg.it) > 0 ? Number(cfg.it) : ITER_LEGACY);

async function hash(pin, saltHex, iter) {
  const salt = unhex(saltHex);
  const key = await crypto.subtle.importKey("raw", enc.encode(String(pin)), "PBKDF2", false, ["deriveBits"]);
  const bits = await crypto.subtle.deriveBits({ name: "PBKDF2", hash: "SHA-256", salt, iterations: iter || ITER_LEGACY }, key, 256);
  return hex(bits);
}
const randHex = (n) => { const a = new Uint8Array(n); crypto.getRandomValues(a); return hex(a); };

// ── LA CASSETTA ──────────────────────────────────────────────────────────────
// Chiave AES-256 derivata dal PIN REALE con un dominio diverso da quello del
// verificatore ("kf:"), così l'impronta salvata non aiuta ad aprire la cassetta.
async function deriveWrap(pin, saltHex, iter) {
  const base = await crypto.subtle.importKey("raw", enc.encode("kf:" + String(pin)), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", hash: "SHA-256", salt: unhex(saltHex), iterations: iter || ITER_LEGACY },
    base, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"],
  );
}
async function boxWrite(key, data) {
  if (!key) return;
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, enc.encode(JSON.stringify(data)));
  try { sessionStorage.setItem(BOX, JSON.stringify({ iv: hex(iv), ct: hex(ct) })); } catch { /* niente */ }
}
async function boxRead(key) {
  if (!key) return null;
  try {
    const o = JSON.parse(sessionStorage.getItem(BOX) || "null");
    if (!o || !o.iv || !o.ct) return null;
    const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv: unhex(o.iv) }, key, unhex(o.ct));
    return JSON.parse(new TextDecoder().decode(pt));
  } catch { return null; }
}
function tokensNow() {
  const out = {};
  for (const k of TOK_KEYS) {
    try { const v = sessionStorage.getItem(k) ?? localStorage.getItem(k); if (v != null) out[k] = v; } catch { /* niente */ }
  }
  return out;
}
/// Aggiorna il file chiave dentro la cassetta senza perdere i token (lo chiama vault.js
/// a ogni cambio del file chiave, per esempio dopo una rotazione).
export function stashKeyfile(kf) {
  const key = wrapKey;
  if (!key || !kf) return;
  boxRead(key).then((old) => boxWrite(key, { ...(old || {}), kf })).catch(() => {});
}

/// Tutto ciò che è vivo adesso: token, identità e file chiave in chiaro. Si azzera sia
/// quando si blocca sia quando si entra con il PIN di copertura — dentro l'esca non deve
/// esistere nulla del profilo vero, nemmeno leggendo la console.
function wipeLive() {
  try {
    for (const k of TOK_KEYS) sessionStorage.removeItem(k);
    sessionStorage.removeItem(RT_PLAIN);
  } catch { /* niente */ }
  try { vault.wipe(); } catch { /* niente */ }
}

/// C'è una cassetta sigillata in questa scheda? Non dice di CHI è né cosa contiene:
/// per chi guarda è un blocco cifrato che potrebbe appartenere a qualunque profilo.
export const hasSealedBox = () => {
  try { return !!sessionStorage.getItem(BOX); } catch { return false; }
};

export function lockConfig() {
  try { return JSON.parse(localStorage.getItem(K) || "null"); } catch { return null; }
}
export const lockEnabled = () => !!lockConfig();

export async function enableLock(realPin, decoyPin) {
  const salt = randHex(16);
  const cfg = {
    salt, it: ITER,
    real: await hash(realPin, salt, ITER),
    decoy: await hash(decoyPin, salt, ITER),
    at: new Date().toISOString(),
  };
  localStorage.setItem(K, JSON.stringify(cfg));
  wrapKey = await deriveWrap(realPin, salt, ITER);
  sessState = "real";
  try { sessionStorage.setItem(S, "real"); } catch { /* niente */ }
  // La cassetta si scrive PRIMA di togliere le copie in chiaro.
  await boxWrite(wrapKey, { kf: vault.get(), tok: tokensNow() });
  tokensToSession();
  try { sessionStorage.removeItem(RT_PLAIN); } catch { /* niente */ }
  return true;
}
export function disableLock() {
  tokensToLocal();
  const kf = vault.get();
  try { localStorage.removeItem(K); sessionStorage.removeItem(S); sessionStorage.removeItem(BOX); } catch { /* niente */ }
  wrapKey = null;
  sessState = "real";
  // Senza lucchetto il file chiave torna a sopravvivere al ricaricamento della scheda.
  try { if (kf) vault.set(kf); } catch { /* niente */ }
}

/// "real" | "decoy" | null — il verdetto per il PIN inserito.
export async function tryPin(pin) {
  const cfg = lockConfig();
  if (!cfg) return "real";
  const it = iterOf(cfg);
  const h = await hash(pin, cfg.salt, it);
  if (h === cfg.real) {
    wrapKey = await deriveWrap(pin, cfg.salt, it);
    sessState = "real";
    try { sessionStorage.setItem(S, "real"); } catch { /* niente */ }
    // Si riapre la cassetta: file chiave e token tornano vivi SOLO ora.
    const box = await boxRead(wrapKey);
    if (box) {
      if (box.kf) { try { vault.set(box.kf); } catch { /* niente */ } }
      for (const k of TOK_KEYS) {
        if (box.tok && box.tok[k] != null) { try { sessionStorage.setItem(k, box.tok[k]); } catch { /* niente */ } }
      }
    }
    // Chi aspettava il file chiave può smettere: è tornato in memoria.
    try { window.dispatchEvent(new Event("lattice:vault")); } catch { /* niente */ }
    return "real";
  }
  if (h === cfg.decoy) {
    wrapKey = null;
    sessState = "decoy";
    try { sessionStorage.setItem(S, "decoy"); } catch { /* niente */ }
    // La sessione vera non entra nell'esca: né token, né identità, né chiave privata.
    wipeLive();
    try { window.dispatchEvent(new Event("lattice:vault")); } catch { /* niente */ }
    return "decoy";
  }
  return null;
}

/// Stato della scheda corrente: chiuso finché non si inserisce un PIN.
export function sessionState() {
  // VIA DI PROVA, spenta in produzione: serve all'automazione dei test per superare il
  // lucchetto senza toccarne la logica. `REACT_APP_E2E` non è definita nelle build
  // pubblicate, quindi questo ramo lì non esiste nemmeno.
  if (process.env.REACT_APP_E2E === "1") return "real";
  if (!lockEnabled()) return "real";
  return sessState || "locked";
}
export function relock() {
  // Prima si mette al sicuro ciò che serve per riaprire col PIN reale...
  const key = wrapKey;
  if (key) boxWrite(key, { kf: vault.get(), tok: tokensNow() }).catch(() => {});
  // ...poi non resta niente di vivo: né stato, né chiave della cassetta, né token, né
  // file chiave in chiaro. Da qui nessuna chiamata al server passa e nessuna sessione
  // può essere rigenerata: la chiave privata non è più raggiungibile.
  sessState = null;
  wrapKey = null;
  try { sessionStorage.removeItem(S); } catch { /* niente */ }
  wipeLive();
}
